import React, { useEffect, useState } from 'react';
import { getErrorMessage } from '@/lib/utils';
import { useI18n } from '../contexts/I18nContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Search, 
  Building2, 
  MapPin, 
  Phone, 
  Globe,
  CheckCircle,
  ShoppingCart,
  Handshake,
  Clock,
  BadgeCheck,
  Users,
  List
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DirectoryPage = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [myRelationships, setMyRelationships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [accessDenied, setAccessDenied] = useState(false);
  
  // Custom labels dal tenant
  const [customLabels, setCustomLabels] = useState<any>({});

  // Carica labels personalizzate
  useEffect(() => {
    const fetchLabels = async () => {
      try {
        const res = await axios.get(`${API}/associations/my-association/labels`);
        if (res.data.labels) setCustomLabels(res.data.labels);
      } catch { /* non è utente associazione */ }
    };
    fetchLabels();
  }, []);

  // Helper per label
  const pageTitle = customLabels.directory_title || t('directory.title');
  const pageSubtitle = customLabels.directory_subtitle || 'Trova fornitori e attiva collaborazioni commerciali';
  const tabAll = customLabels.directory_tab_all || 'Tutti i Fornitori';
  const tabMine = customLabels.directory_tab_mine || 'I Miei Fornitori';
  const tabPending = customLabels.directory_tab_pending || 'In Attesa';

  // Verifica accesso al catalogo per utenti groupage
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const res = await axios.get(`${API}/associations/my-association/menu`);
        if (res.data.is_association_user) {
          const menu = res.data.menu || {};
          // Se directory_fornitori non è visibile, nega accesso
          if (menu.directory_fornitori?.visible === false) {
            setAccessDenied(true);
            toast.error('Accesso al catalogo non abilitato per la tua associazione');
            navigate('/dashboard');
            return;
          }
        }
      } catch {
        // Non è utente associazione, accesso consentito
      }
    };
    checkAccess();
  }, [navigate]);

  useEffect(() => {
    if (!accessDenied) {
      fetchData();
    }
  }, [search, typeFilter, accessDenied]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (typeFilter && typeFilter !== 'all') params.append('type', typeFilter);
      
      // Fetch suppliers and relationships in parallel
      const [suppliersRes, relationshipsRes] = await Promise.all([
        axios.get(`${API}/directory?${params.toString()}`),
        axios.get(`${API}/relationships`)
      ]);
      
      setSuppliers(suppliersRes.data);
      setMyRelationships(relationshipsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  // Determina lo stato della relazione con un fornitore
  const getRelationshipStatus = (supplierId) => {
    const rel = myRelationships.find(r => r.seller_company_id === supplierId);
    if (!rel) return null;
    return rel.status; // 'pending', 'active', 'rejected'
  };

  // Filtra fornitori in base al tab
  const getFilteredSuppliers = () => {
    if (activeTab === 'my-suppliers') {
      // Fornitori con relazione approvata/attiva
      const activeIds = myRelationships
        .filter(r => r.status === 'active' || r.status === 'approved')
        .map(r => r.seller_company_id);
      return suppliers.filter(s => activeIds.includes(s.id));
    } else if (activeTab === 'pending') {
      // Fornitori con richiesta in attesa
      const pendingIds = myRelationships
        .filter(r => r.status === 'pending')
        .map(r => r.seller_company_id);
      return suppliers.filter(s => pendingIds.includes(s.id));
    }
    // Tab 'all' - mostra tutti
    return suppliers;
  };

  // Conta per i badge delle tab
  const mySupplierCount = myRelationships.filter(r => r.status === 'active' || r.status === 'approved').length;
  const pendingCount = myRelationships.filter(r => r.status === 'pending').length;

  const handleRequestRelationship = async () => {
    if (!selectedSupplier) return;
    
    setRequestLoading(true);
    try {
      await axios.post(`${API}/relationships`, {
        seller_company_id: selectedSupplier.id,
        qualification_data: {}
      });
      toast.success(t('directory.requestSent'));
      setShowRequestDialog(false);
      setSelectedSupplier(null);
      fetchData(); // Ricarica per aggiornare lo stato
    } catch (error) {
      toast.error(getErrorMessage(error, t('common.error')));
    } finally {
      setRequestLoading(false);
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      producer: t('directory.producer'),
      distributor: t('directory.distributor'),
      agent: t('directory.agent'),
      other: t('directory.other')
    };
    return labels[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      producer: 'bg-green-500/10 text-green-600 dark:text-green-400',
      distributor: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      agent: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
      other: 'bg-gray-500/10 text-gray-600 dark:text-gray-400'
    };
    return colors[type] || colors.other;
  };

  // Renderizza il pulsante/stato in base alla relazione
  const renderSupplierAction = (supplier) => {
    const status = getRelationshipStatus(supplier.id);
    
    if (status === 'active' || status === 'approved') {
      // Relazione attiva - mostra pulsanti per ordinare e vedere listino
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
            <BadgeCheck className="h-4 w-4" />
            <span className="font-medium">Fornitore Attivo</span>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              className="flex-1"
              onClick={() => navigate(`/catalog/${supplier.id}`)}
              data-testid={`browse-catalog-${supplier.id}`}
            >
              <List className="mr-2 h-4 w-4" />
              Listino
            </Button>
            <Button 
              className="flex-1"
              onClick={() => navigate(`/orders/new?seller=${supplier.id}`)}
              data-testid={`order-from-${supplier.id}`}
            >
              <ShoppingCart className="mr-2 h-4 w-4" />
              Ordina
            </Button>
          </div>
        </div>
      );
    }
    
    if (status === 'pending') {
      // Richiesta in attesa
      return (
        <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400 p-3 rounded-lg bg-amber-500/10">
          <Clock className="h-4 w-4" />
          <span>Richiesta in valutazione</span>
        </div>
      );
    }
    
    if (status === 'rejected') {
      // Richiesta rifiutata - può riprovare
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
            <span>Richiesta non approvata</span>
          </div>
          <Button 
            variant="outline"
            className="w-full"
            onClick={() => {
              setSelectedSupplier(supplier);
              setShowRequestDialog(true);
            }}
            data-testid={`retry-request-${supplier.id}`}
          >
            <Handshake className="mr-2 h-4 w-4" />
            Richiedi Nuovamente
          </Button>
        </div>
      );
    }
    
    // Nessuna relazione - può richiedere
    return (
      <Button 
        className="w-full"
        variant="outline"
        onClick={() => {
          setSelectedSupplier(supplier);
          setShowRequestDialog(true);
        }}
        data-testid={`request-access-${supplier.id}`}
      >
        <Handshake className="mr-2 h-4 w-4" />
        Richiedi Accesso Catalogo
      </Button>
    );
  };

  const filteredSuppliers = getFilteredSuppliers();

  return (
    <div className="space-y-6" data-testid="directory-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold">{pageTitle}</h1>
        <p className="text-muted-foreground mt-1">
          {pageSubtitle}
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="all" className="gap-2">
            <Building2 className="h-4 w-4" />
            {tabAll}
          </TabsTrigger>
          <TabsTrigger value="my-suppliers" className="gap-2">
            <BadgeCheck className="h-4 w-4" />
            {tabMine}
            {mySupplierCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">{mySupplierCount}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="pending" className="gap-2">
            <Clock className="h-4 w-4" />
            {tabPending}
            {pendingCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">{pendingCount}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('directory.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="directory-search"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="directory-type-filter">
                <SelectValue placeholder={t('directory.allTypes')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('directory.allTypes')}</SelectItem>
                <SelectItem value="producer">{t('directory.producer')}</SelectItem>
                <SelectItem value="distributor">{t('directory.distributor')}</SelectItem>
                <SelectItem value="agent">{t('directory.agent')}</SelectItem>
                <SelectItem value="other">{t('directory.other')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Suppliers Grid */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredSuppliers.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSuppliers.map((supplier) => (
                <Card 
                  key={supplier.id} 
                  className={`transition-colors ${
                    getRelationshipStatus(supplier.id) === 'approved' 
                      ? 'border-green-500/30 bg-green-500/5' 
                      : 'hover:border-primary/50'
                  }`}
                  data-testid={`supplier-card-${supplier.id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        {supplier.logo_url ? (
                          <img 
                            src={supplier.logo_url} 
                            alt={supplier.name}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Building2 className="h-6 w-6 text-primary" />
                          </div>
                        )}
                        <div>
                          <CardTitle className="text-lg font-heading flex items-center gap-2">
                            {supplier.name}
                            {supplier.is_accredited && (
                              <CheckCircle className="h-4 w-4 text-primary" />
                            )}
                          </CardTitle>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTypeColor(supplier.type)}`}>
                            {getTypeLabel(supplier.type)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {supplier.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {supplier.description}
                      </p>
                    )}
                    
                    <div className="space-y-2 text-sm">
                      {supplier.address && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <MapPin className="h-4 w-4" />
                          <span className="truncate">{supplier.address}</span>
                        </div>
                      )}
                      {supplier.phone && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Phone className="h-4 w-4" />
                          <span>{supplier.phone}</span>
                        </div>
                      )}
                      {supplier.website && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Globe className="h-4 w-4" />
                          <a 
                            href={supplier.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="truncate hover:text-primary"
                          >
                            {supplier.website}
                          </a>
                        </div>
                      )}
                    </div>

                    {supplier.categories?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {supplier.categories.slice(0, 3).map((cat) => (
                          <Badge key={cat} variant="secondary" className="text-xs">
                            {cat}
                          </Badge>
                        ))}
                        {supplier.categories.length > 3 && (
                          <Badge variant="secondary" className="text-xs">
                            +{supplier.categories.length - 3}
                          </Badge>
                        )}
                      </div>
                    )}

                    <div className="mt-4 pt-4 border-t">
                      {renderSupplierAction(supplier)}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                {activeTab === 'my-suppliers' ? (
                  <>
                    <Users className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground text-center">
                      Non hai ancora fornitori attivi.<br />
                      Esplora la directory e richiedi l'accesso ai cataloghi.
                    </p>
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => setActiveTab('all')}
                    >
                      Esplora Fornitori
                    </Button>
                  </>
                ) : activeTab === 'pending' ? (
                  <>
                    <Clock className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Nessuna richiesta in attesa</p>
                  </>
                ) : (
                  <>
                    <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">{t('directory.noResults')}</p>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Request Access Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Handshake className="h-5 w-5" />
              Richiedi Accesso Catalogo
            </DialogTitle>
            <DialogDescription>
              {selectedSupplier && (
                <span>
                  Vuoi richiedere l'accesso al catalogo di <strong>{selectedSupplier.name}</strong>?
                  <br /><br />
                  Una volta approvata la richiesta, potrai visualizzare i prodotti e i listini dedicati, e creare ordini.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowRequestDialog(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleRequestRelationship}
              disabled={requestLoading}
              data-testid="confirm-request-access"
            >
              {requestLoading ? t('common.loading') : 'Invia Richiesta'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DirectoryPage;
